/**
 * Personal Map PM-3 — Add To Journey 純 frontend handoff。
 *
 * CANONICAL_LOCATION_ID=locationId
 * HANDOFF_TARGET=EXISTING_JOURNEY_PLANNING_ACTIVITY_FLOW
 * PERSONAL_MAP_ATTACHMENT_CONTRACT=NONE
 * PUBLICATION_LINKAGE=NONE
 * AUTO_CREATE_ACTIVITY=NO
 * USER_CONFIRMATION_REQUIRED=YES
 */

const WRITABLE_LIFECYCLES = new Set(['DRAFT', 'READY', 'TRAVELING']);
const OWNER_ROLE = 'OWNER';

export const PERSONAL_MAP_HANDOFF_STATE_KEY = 'personalMapJourneyHandoff';
export const PERSONAL_MAP_HANDOFF_SESSION_KEY = 'travel.personalMap.journeyHandoff';

const SOURCE_CONTEXTS = new Set(['personal-map', 'journey-create-continuation', 'planning']);

/** In-memory consume-once registry（同頁 refresh 會清空；搭配 replace 清 state）。 */
const consumedNonces = new Set();

function snowflakeId(value) {
  if (value == null || value === '') return null;
  const id = String(value);
  return /^[1-9]\d*$/.test(id) ? id : null;
}

function createNonce() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `pm3-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function normalizeHandoffSourceContext(value) {
  const text = String(value || '').trim().toLowerCase();
  if (SOURCE_CONTEXTS.has(text)) return text;
  return 'personal-map';
}

/**
 * 建立一次性 handoff。locationId 必填；不接受 providerPlaceId／publicationId。
 */
export function createPersonalMapJourneyHandoff({
  locationId,
  displayName,
  category,
  sourceContext,
  returnTo,
  journeyId,
  journeyDayId,
} = {}) {
  const canonicalLocationId = snowflakeId(locationId);
  if (!canonicalLocationId) return null;

  const handoff = {
    locationId: canonicalLocationId,
    displayName: String(displayName || '').trim() || null,
    category: category == null || String(category).trim() === ''
      ? null
      : String(category).trim().toUpperCase(),
    sourceContext: normalizeHandoffSourceContext(sourceContext),
    returnTo: String(returnTo || '/me/personal-map').trim() || '/me/personal-map',
    handoffNonce: createNonce(),
  };

  const jid = snowflakeId(journeyId);
  if (jid) handoff.journeyId = jid;
  const did = snowflakeId(journeyDayId);
  if (did) handoff.journeyDayId = did;

  return handoff;
}

/** 從 router state 或純物件讀取並正規化；無效則 null。 */
export function parsePersonalMapJourneyHandoff(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const nested = raw[PERSONAL_MAP_HANDOFF_STATE_KEY];
  const candidate = nested && typeof nested === 'object' ? nested : raw;
  if (candidate.providerPlaceId != null) return null;
  if (candidate.publicationId != null) return null;
  if (candidate.ownerMemberId != null) return null;

  const locationId = snowflakeId(candidate.locationId);
  if (!locationId) return null;

  const nonce = String(candidate.handoffNonce || '').trim();
  if (!nonce) return null;

  const handoff = {
    locationId,
    displayName: String(candidate.displayName || '').trim() || null,
    category: candidate.category == null || String(candidate.category).trim() === ''
      ? null
      : String(candidate.category).trim().toUpperCase(),
    sourceContext: normalizeHandoffSourceContext(candidate.sourceContext),
    returnTo: String(candidate.returnTo || '/me/personal-map').trim() || '/me/personal-map',
    handoffNonce: nonce,
  };

  const jid = snowflakeId(candidate.journeyId);
  if (jid) handoff.journeyId = jid;
  const did = snowflakeId(candidate.journeyDayId);
  if (did) handoff.journeyDayId = did;

  return handoff;
}

export function wrapHandoffInLocationState(handoff, extra = {}) {
  const parsed = parsePersonalMapJourneyHandoff(handoff);
  if (!parsed) return { ...extra };
  return {
    ...extra,
    [PERSONAL_MAP_HANDOFF_STATE_KEY]: parsed,
  };
}

export function readHandoffFromLocationState(state) {
  if (!state || typeof state !== 'object') return null;
  return parsePersonalMapJourneyHandoff(state[PERSONAL_MAP_HANDOFF_STATE_KEY] || state);
}

export function stripHandoffFromLocationState(state) {
  if (!state || typeof state !== 'object') return {};
  const next = { ...state };
  delete next[PERSONAL_MAP_HANDOFF_STATE_KEY];
  return next;
}

/** 短生命週期 sessionStorage（僅 Create Journey 跨頁備援；非永久）。 */
export function persistHandoffSession(handoff) {
  const parsed = parsePersonalMapJourneyHandoff(handoff);
  if (!parsed || typeof sessionStorage === 'undefined') return false;
  try {
    sessionStorage.setItem(PERSONAL_MAP_HANDOFF_SESSION_KEY, JSON.stringify(parsed));
    return true;
  } catch {
    return false;
  }
}

export function readHandoffSession() {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(PERSONAL_MAP_HANDOFF_SESSION_KEY);
    if (!raw) return null;
    return parsePersonalMapJourneyHandoff(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function clearHandoffSession() {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(PERSONAL_MAP_HANDOFF_SESSION_KEY);
  } catch {
    // ignore
  }
}

export function isHandoffNonceConsumed(nonce) {
  const key = String(nonce || '').trim();
  return Boolean(key && consumedNonces.has(key));
}

/**
 * 一次性 consume。回傳 true 表示首次成功；已 consume／無效回傳 false。
 */
export function consumeHandoffNonce(nonce) {
  const key = String(nonce || '').trim();
  if (!key) return false;
  if (consumedNonces.has(key)) return false;
  consumedNonces.add(key);
  return true;
}

/** 測試用：重置 in-memory consume registry。 */
export function resetHandoffConsumeRegistryForTests() {
  consumedNonces.clear();
}

/**
 * Owner 可編輯 private Journey：requesterRole=OWNER ∩ writable lifecycle。
 * 對齊 planningCapabilities.canCreateActivity，但額外限制 OWNER（本 AUTH）。
 */
export function isOwnerEditableJourney(journey) {
  if (!journey || typeof journey !== 'object') return false;
  const journeyId = snowflakeId(journey.journeyId);
  if (!journeyId) return false;
  const role = String(journey.requesterRole || '').toUpperCase();
  const lifecycle = String(journey.lifecycleStatus || '').toUpperCase();
  return role === OWNER_ROLE && WRITABLE_LIFECYCLES.has(lifecycle);
}

export function filterOwnerEditableJourneys(list) {
  return (Array.isArray(list) ? list : []).filter(isOwnerEditableJourney);
}

/**
 * Personal Map entry 是否可加入行程。
 */
export function canAddPersonalMapEntryToJourney(entry, { actionPending = false } = {}) {
  if (!entry || typeof entry !== 'object') return false;
  if (actionPending) return false;
  if (!snowflakeId(entry.locationId)) return false;
  if (entry.hidden) return false;
  const status = String(entry.status || '').toUpperCase();
  if (status !== 'ACTIVE') return false;
  return true;
}

export function locationNodeFromHandoff(handoff) {
  const parsed = parsePersonalMapJourneyHandoff(handoff);
  if (!parsed) return null;
  return {
    locationId: parsed.locationId,
    displayName: parsed.displayName || '已選地點',
  };
}
